// Fixed taxonomy of WHY a thumbs-down finding was wrong. Single source of
// truth for the LLM classifier (api), the deterministic fallback (api), and
// the portal chip. `maps_to` names the reviewer lever a calibration PR should
// target — see draft-context / draftEdit. `other` is the required catch-all so
// every 👎 is assigned and per-reviewer dominance percentages are honest.

export interface RootCauseBucket {
  id: string;
  label: string;
  description: string;
  maps_to: string;
}

export const OTHER_BUCKET_ID = 'other';

export const ROOT_CAUSE_BUCKETS = [
  {
    id: 'future-speculation',
    label: 'Blocked on future / later-PR state',
    description: 'Flagged what a later PR or future deploy might do, not what this diff actually changes.',
    maps_to: 'speculation guard: only flag what the diff itself changes',
  },
  {
    id: 'author-acknowledged',
    label: 'Re-raised what the author already named',
    description: 'Re-raised a risk the PR body, test plan, or a code comment had already acknowledged.',
    maps_to: 'author-acknowledgement rule: honor author-declared deferrals',
  },
  {
    id: 'cross-repo-unverifiable',
    label: 'External / cross-repo condition not in the diff',
    description: 'Conditioned the finding on deploy ordering or another repo the reviewer cannot see in diff-only context.',
    maps_to: 'diff-only-context note: do not assume cross-repo or deploy-ordering state',
  },
  {
    id: 'severity-inflation',
    label: '"Be aware" note marked blocking',
    description: 'A legitimate informational note was assigned a blocking severity.',
    maps_to: 'severity calibration: reserve blocking for diff-evident breakage',
  },
  {
    id: 'out-of-scope',
    label: "Flagged outside the PR's intent",
    description: 'Flagged pre-existing or unrelated code the PR did not introduce or change.',
    maps_to: 'scope rule: confine findings to lines the PR changes',
  },
  {
    id: 'false-positive-detection',
    label: 'Technical claim is simply wrong',
    description: 'The reviewer misread the diff; the asserted problem does not exist.',
    maps_to: 'reviewer detection accuracy',
  },
  {
    // Distinct from false-positive-detection on purpose: the reasoning here is
    // CORRECT and the finding still shipped. Bucketing these as a detection
    // failure points calibration at the one thing that worked — observed twice
    // in 2026-33 and 2026-32, so the lever is the publication gate, not accuracy.
    id: 'non-finding',
    label: 'Argues itself down to nothing',
    description: 'The finding reaches the correct conclusion that there is no issue, and posts anyway.',
    maps_to: 'publication gate: drop a finding whose own text concedes no issue',
  },
  {
    // The 2026-32 supportability case: "not factually wrong, it was unverifiable".
    // The reviewer could not see the context it was asking about, so it asked the
    // author to confirm its premise. Accurate, unactionable, and pure noise.
    id: 'unverifiable-premise',
    label: 'Asked the author to confirm its own premise',
    description: 'Could not verify a premise from the diff, so it asked the author to confirm rather than dropping it.',
    maps_to: 'confirm-my-premise drop rule: a finding you cannot verify is not a finding',
  },
  {
    id: OTHER_BUCKET_ID,
    label: 'Other / uncategorized',
    description: 'Down-voted for a reason that does not fit a known bucket.',
    maps_to: '',
  },
] as const satisfies readonly RootCauseBucket[];

export type RootCauseBucketId = typeof ROOT_CAUSE_BUCKETS[number]['id'];

export const ROOT_CAUSE_BUCKET_IDS: ReadonlySet<string> = new Set(
  ROOT_CAUSE_BUCKETS.map((b) => b.id),
);

export function getRootCauseBucket(id: string): RootCauseBucket | undefined {
  return ROOT_CAUSE_BUCKETS.find((b) => b.id === id);
}
